import classNames from "classnames";
import React, { DragEventHandler } from "react";

import styles from "./tool-palette.module.scss";

export const PALETTE_DRAG_ID = "DragId";

const dragStart: DragEventHandler<HTMLSpanElement> = (event) => {
  event.dataTransfer.setData(PALETTE_DRAG_ID, event.currentTarget.id);
};

/** ================================
 * Player Items
  ================================ */
export enum PlayerStyle {
  NORMAL,
  BORDER,
  FILL,
  OFFSET_POSSESSION,
}

export interface PlayerProp<TId extends string = string> {
  id: TId;
  label: string;
  style?: PlayerStyle;
}

export interface PositionsProps<TId extends string = string> {
  positions: PlayerProp<TId>[][];
  spacing?: "center";
}

export const Positions = <TId extends string = string>({ positions, spacing }: PositionsProps<TId>) => {
  return (
    <>
      {positions.map((row, rowKey) => (
        <div className={styles.row} style={{ ...(spacing ? { justifyContent: spacing } : {}) }} key={rowKey}>
          {row.map((item, itemKey) => {
            const { id, label, style = PlayerStyle.NORMAL } = item;
            return (
              <span
                id={id}
                key={itemKey}
                className={classNames(styles.item, styles.positionItem, {
                  [styles?.circleBorder || ""]: style === PlayerStyle.BORDER,
                  [styles?.fill || ""]: style === PlayerStyle.FILL,
                })}
                draggable
                onDragStart={dragStart}
              >
                <span>
                  {label}
                  {style === PlayerStyle.OFFSET_POSSESSION && <span className={styles.offsetPossession}>&#x2022;</span>}
                </span>
              </span>
            );
          })}
        </div>
      ))}
    </>
  );
};

/** ================================
 * Action Renderer
  ================================ */
export interface ActionItem<TId extends string = string> {
  id: TId;
  label: string;
  iconUrl: string;
}
export type ReadOnlyActionItem<TId extends string = string> = readonly ActionItem<TId>[];
export interface ActionItemsProps<TId extends string = string> {
  items: ReadOnlyActionItem<TId>;
}

export const ActionItems = <TId extends string = string>({ items }: ActionItemsProps<TId>) => {
  return (
    <>
      {items.map((item) => (
        <div
          id={item.id}
          key={item.id}
          className={classNames(styles.item, styles.actionItem)}
          draggable
          onDragStart={dragStart}
        >
          <img className={styles.itemIcon} src={item.iconUrl} />
          <span className={styles.itemLabel}>{item.label}</span>
        </div>
      ))}
    </>
  );
};

/** ================================
 * Icon Renderer
  ================================ */
export const IconItems = <TId extends string = string>({ items }: ActionItemsProps<TId>) => {
  return (
    <div className={styles.iconItemContainer}>
      {items.map((item) => (
        <div
          id={item.id}
          key={item.id}
          className={classNames(styles.item, styles.iconItem)}
          draggable
          onDragStart={dragStart}
        >
          <img src={item.iconUrl} alt={item.label} title={item.label} />
        </div>
      ))}
    </div>
  );
};
